import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  NgZone,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';

type CaptureState = 'streaming' | 'captured' | 'error';

/**
 * Live photo capture in a modal dialog, used for the registration "photo" field when the
 * event's proctoring mode is "live" (or the camera tab of "both"). Flow inside the dialog:
 * stream -> capture -> preview the still -> retake / upload. On "Upload" it emits the
 * captured frame as a JPEG File and closes; the parent performs the actual GCS upload.
 */
@Component({
  selector: 'app-photo-capture',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule, TooltipModule],
  templateUrl: './photo-capture.component.html',
  styleUrls: ['./photo-capture.component.css'],
})
export class PhotoCaptureComponent implements OnDestroy {
  private _visible = false;
  @Input() set visible(value: boolean) {
    if (value === this._visible) {
      return;
    }
    this._visible = value;
    if (value) {
      this.onOpen();
    } else {
      this.stopCamera();
    }
  }
  get visible(): boolean {
    return this._visible;
  }
  @Output() visibleChange = new EventEmitter<boolean>();

  /** Emits the captured frame as a JPEG File when the candidate confirms "Upload". */
  @Output() captured = new EventEmitter<File>();

  @ViewChild('video') private videoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') private canvasRef!: ElementRef<HTMLCanvasElement>;

  state: CaptureState = 'streaming';
  errorMessage = '';
  /** True once the live video has frames available (Capture stays disabled until then). */
  videoReady = false;
  capturedPreviewUrl: string | null = null;
  agreedToUpload = false;

  private stream: MediaStream | null = null;
  private capturedBlob: Blob | null = null;
  private startTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private cameraStarting = false;
  private zone = inject(NgZone);

  ngOnDestroy(): void {
    this.stopCamera();
    this.clearPreview();
  }

  /** p-dialog visibility changes (X button / mask click). */
  onDialogVisibleChange(value: boolean): void {
    this._visible = value;
    this.visibleChange.emit(value);
    if (!value) {
      this.stopCamera();
    }
  }

  /** Bound to the <video> (loadeddata) — enables Capture once a real frame is available. */
  onVideoReady(): void {
    this.videoReady = true;
  }

  private onOpen(): void {
    this.state = 'streaming';
    this.videoReady = false;
    this.agreedToUpload = false;
    this.clearPreview();
    this.errorMessage = '';
    // Defer so the dialog content (the <video>) is in the DOM before attaching the stream.
    this.startTimeoutId = setTimeout(() => this.startCamera(), 0);
  }

  async startCamera(): Promise<void> {
    if (!this._visible || this.cameraStarting) {
      return;
    }
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setError('Your browser or device does not support camera capture.');
      return;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    this.cameraStarting = true;
    this.state = 'streaming';
    this.videoReady = false;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      if (!this._visible) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.stream = stream;
      this.attachStream();
    } catch {
      this.setError(
        'Camera access is required to capture your photo. Please allow camera permission and try again.'
      );
    } finally {
      this.cameraStarting = false;
    }
  }

  /** Attach the stream once the <video> exists (it may render a tick after the dialog opens). */
  private attachStream(attempt = 0): void {
    if (!this.stream || !this._visible) {
      return;
    }
    const video = this.videoRef?.nativeElement;
    if (!video) {
      if (attempt < 20) {
        setTimeout(() => this.attachStream(attempt + 1), 50);
      }
      return;
    }
    video.srcObject = this.stream;
    video.muted = true;
    video.setAttribute('playsinline', 'true');
    video.play().catch(() => undefined);
  }

  capture(): void {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas || !video.videoWidth || video.readyState < 2) {
      // Frame not ready yet — ignore the click.
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return;
    }
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          return;
        }
        // toBlob's callback runs outside Angular's zone — re-enter so the state flip
        // (and the preview image / Retake-Upload buttons) renders immediately.
        this.zone.run(() => {
          this.clearPreview();
          this.capturedBlob = blob;
          this.capturedPreviewUrl = URL.createObjectURL(blob);
          this.state = 'captured';
          this.stopCamera();
        });
      },
      'image/jpeg',
      0.92
    );
  }

  retake(): void {
    this.clearPreview();
    this.state = 'streaming';
    this.videoReady = false;
    this.agreedToUpload = false;
    this.startCamera();
  }

  confirmUpload(): void {
    if (!this.capturedBlob || !this.agreedToUpload) {
      return;
    }
    const file = new File([this.capturedBlob], `photo_${new Date().getTime()}.jpg`, {
      type: 'image/jpeg',
    });
    this.captured.emit(file);
    this.close();
  }

  close(): void {
    this._visible = false;
    this.visibleChange.emit(false);
    this.stopCamera();
  }

  private setError(message: string): void {
    this.errorMessage = message;
    this.state = 'error';
    this.stopCamera();
  }

  private stopCamera(): void {
    if (this.startTimeoutId !== null) {
      clearTimeout(this.startTimeoutId);
      this.startTimeoutId = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    const video = this.videoRef?.nativeElement;
    if (video) {
      video.srcObject = null;
    }
  }

  private clearPreview(): void {
    if (this.capturedPreviewUrl) {
      URL.revokeObjectURL(this.capturedPreviewUrl);
      this.capturedPreviewUrl = null;
    }
    this.capturedBlob = null;
  }
}
